import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import './ProfessionalsPage.css';

const ProfessionalsPage: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO Meta tags for this page
  useEffect(() => {
    // Update document title
    document.title = 'Get Your Custom Fitness Plan - Professional Training | TaaFit';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'Get your personalized fitness plan with TaaFit\'s professional coaching. Professional coaching standards with comprehensive assessment, goal mapping, and custom training program. $149 consultation fee.'
      );
    }
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://taafit.com/professionals');
    }

    // Cleanup on unmount
    return () => {
      document.title = 'TaaFit - Personal Fitness Training & Coaching | Transform Your Body 2025-2026';
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          'Transform your fitness journey with TaaFit\'s personalized training programs. Professional fitness coaching with Professional expertise, nutrition planning, and workout scheduling. Join 90+ satisfied clients achieving their fitness goals in 2025-2026.'
        );
      }
      if (canonicalLink) {
        canonicalLink.setAttribute('href', 'https://taafit.com');
      }
    };
  }, []);

  // Discount form state
  const [discountCode, setDiscountCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [discountSuccess, setDiscountSuccess] = useState(false);
  
  // Contact form modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    instagram: ''
  });

  const handleDiscountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    setDiscountError('');
    
    try {
      // Query Supabase for valid discount codes
      const { data: discountData, error: queryError } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('code', discountCode.toUpperCase())
        .eq('is_active', true)
        .single();

      if (queryError || !discountData) {
        setDiscountError('Discount code is not valid');
        return;
      }

      // Log the discount code usage
      // Note: We don't send 'id' or 'submitted_at' - Supabase/PostgreSQL generates these via DEFAULT values
      const { error: insertError } = await supabase
        .from('discount_submissions')
        .insert([{
          discount_code: discountCode.toUpperCase(),
        }]);

      if (insertError) {
        console.error('Error logging discount usage:', insertError);
        // Don't block the user flow for logging errors
      }

      setDiscountSuccess(true);
      // Redirect to consultation Calendly (different from main one)
      // Using window.location.href instead of window.open for Safari compatibility
      // Safari blocks popups that aren't directly triggered by user interaction
      setTimeout(() => {
        window.location.href = 'https://calendly.com/taafit-info/professional-consultation';
      }, 1500);

    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountError('Something went wrong. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleContactClick = () => {
    setIsModalOpen(true);
    setContactError(''); // Clear any previous contact errors
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setContactError('');

    try {
      // Require at least one social account
      if (!formData.linkedin && !formData.instagram) {
        setContactError('Please provide at least one social account (LinkedIn or Instagram).');
        setIsSubmitting(false);
        return;
      }

      // Save contact form data to Supabase
      // Note: We don't send 'id' or 'submitted_at' - Supabase/PostgreSQL generates these via DEFAULT values
      const { error: insertError } = await supabase
        .from('contact_submissions')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          // Keep NOT NULL columns satisfied even if removed from UI
          experience_level: 'unspecified',
          training_goals: 'unspecified',
          // Pack socials into additional_info field since dedicated columns do not exist
          additional_info: `${formData.linkedin ? `LinkedIn: ${formData.linkedin}` : ''}${formData.linkedin && formData.instagram ? ' | ' : ''}${formData.instagram ? `Instagram: ${formData.instagram}` : ''}`,
        }]);

      if (insertError) {
        throw insertError;
      }

      // Reset form and close modal
      setFormData({
        name: '',
        email: '',
        phone: '',
        linkedin: '',
        instagram: ''
      });
      setIsModalOpen(false);
      
      // Show success message
      setContactSuccess(true);
      setTimeout(() => setContactSuccess(false), 5000);

    } catch (error) {
      console.error('Error submitting contact form:', error);
      setContactError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="professionals-page">
      {/* Header with back navigation */}
      <motion.header 
        className="professionals-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="header-container">
          <button 
            className="back-button"
            onClick={() => window.history.back()}
          >
            ← Back to Home
          </button>
          {/* <h1>Join Professionals</h1> */}
        </div>
      </motion.header>

      {/* Main Content - Split Layout */}
      <div className="professionals-content">
        <div className="content-container">
          
          {/* Left Half - Discount Code Form */}
          <motion.div 
            className="discount-section"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="section-header">
              <h2>Professional Discount</h2>
              <p>Enter your professional discount code to access exclusive rates</p>
            </div>

            <form className="discount-form" onSubmit={handleDiscountSubmit}>
              <div className="form-group">
                <label htmlFor="discountCode">Discount Code</label>
                <input
                  type="text"
                  id="discountCode"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Enter your code"
                className={`form-input ${discountError ? 'error' : ''} ${discountSuccess ? 'success' : ''}`}
                disabled={isValidating || discountSuccess}
                />
              </div>

              {discountError && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {discountError}
                </motion.div>
              )}

              {discountSuccess && (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  ✓ Code verified! Redirecting to consultation...
                </motion.div>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={!discountCode || isValidating || discountSuccess}
              >
                {isValidating ? 'Validating...' : 'Apply Discount'}
              </button>
            </form>

            <div className="discount-info">
              <h3>Professional Benefits</h3>
              <ul>
                <li>Exclusive discount rates</li>
                <li>Priority booking access</li>
                <li>Advanced training programs</li>
                <li>Professional certification support</li>
              </ul>
            </div>
          </motion.div>

          {/* Right Half - Get a Step-by-Step plan  */}
          <motion.div 
            className="purchase-section"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <div className="section-header">
              <h2>Get a Step-by-Step plan </h2>
              <p>for your specific situation and goals</p>
            </div>

            <div className="training-card">
              <div className="training-image">
                <img
                  src="/images/hero/athlete-portrait.png"
                  alt="Athlete lifting barbell"
                  className="training-photo"
                />
              </div>

              <div className="training-details">
                {/* <h3>Get a Step-by-Step plan </h3> */}
                <div className="training-features">
                  <div className="feature">
                    <span className="feature-icon">💪</span>
                    <span>Personalized Fitness & Lifestyle Assessment</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🎯</span>
                    <span>Goal Mapping & Habit Discovery Session</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">📊</span>
                    <span>Movement & Nutrition Check-Up</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🏆</span>
                    <span>Your Custom Fitness Plan</span>
                  </div>
                </div>

                <div className="pricing">
                  {/* <span className="price-label">From</span> */}
                  <span className="price">$149</span>
                </div>

                <button 
                  className="contact-btn"
                  onClick={handleContactClick}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Contact Form Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Contact Us</h3>
                <button
                  className="modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  ×
                </button>
              </div>

              <form className="contact-form" onSubmit={handleFormSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
              <div className="form-group">
                <label htmlFor="linkedin">LinkedIn Profile</label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="https://www.linkedin.com/in/username"
                />
              </div>
                </div>

            <div className="form-group">
              <label htmlFor="instagram">Instagram Handle or URL</label>
              <input
                type="text"
                id="instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                className="form-input"
                placeholder="@username or https://instagram.com/username"
              />
            </div>

                {contactError && (
                  <motion.div 
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {contactError}
                  </motion.div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                  disabled={
                    isSubmitting ||
                    !formData.name ||
                    !formData.email ||
                    (!formData.linkedin && !formData.instagram)
                  }
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Message */}
      <AnimatePresence>
        {contactSuccess && !isModalOpen && (
          <motion.div
            className="success-toast"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            ✓ Thank you! Your application has been submitted successfully. We'll get back to you soon!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfessionalsPage;
